/**
 * The sub-questions of a multi-part problem.
 *
 * A third of the open questions in the bank put the actual task in
 * `parts[].prompt` and leave `statement` as a lead-in ("Motivera tydligt dina
 * svar i nedanstående fall."), so a surface that renders only the statement
 * shows a student no question at all. Numeric questions already print their
 * per-part prompt next to each input (`practice/AnswerArea.tsx`); this is the
 * same list for every surface that has no input to hang it on — the practice
 * card, the exam session and grade pages, the archive and the teacher's
 * question expander.
 *
 * Renders nothing when no part carries a prompt: a single-part problem whose
 * statement already *is* the question must not grow a stray "a)".
 */
import clsx from "clsx";
import type { Figure as FigureData, Localized as LocalizedValue } from "@shared/types";
import { useLang } from "@client/i18n";
import { formatPoints } from "@client/lib/format";
import { Figure } from "./Figure";
import { Localized } from "./Localized";

export interface QuestionPart {
  id: string;
  prompt?: LocalizedValue | undefined;
  /** Open-question parts carry their own points; numeric parts carry a unit instead. */
  points?: number | undefined;
}

export interface QuestionPartsProps {
  parts: readonly QuestionPart[];
  /**
   * The question's figures. Only those tagged with a `partId` are drawn here —
   * the caller keeps rendering the untagged ones with the statement.
   */
  figures?: readonly FigureData[];
  className?: string;
}

/** True when this list would render something — lets a caller drop a wrapper. */
export function hasPartPrompts(parts: readonly QuestionPart[]): boolean {
  return parts.some((part) => part.prompt);
}

export function QuestionParts({ parts, figures, className }: QuestionPartsProps) {
  const { lang } = useLang();
  if (!hasPartPrompts(parts)) return null;

  return (
    <ol className={clsx("mt-4 space-y-3", className)}>
      {parts.map((part) => {
        const partFigures = (figures ?? []).filter((figure) => figure.partId === part.id);
        return (
          <li key={part.id} className="flex gap-2">
            {/* `leading-7` matches the prose line height, and the prompt's
                first block drops its top margin, so "a)" sits on the same
                baseline as the first line of the prompt. */}
            <span className="shrink-0 font-medium leading-7 text-ink-800 dark:text-ink-100">
              {part.id})
            </span>
            <div className="min-w-0 flex-1">
              <Localized value={part.prompt} variant="compact" className="[&>*:first-child]:mt-0" />
              {partFigures.map((figure, index) => (
                <Figure key={index} figure={figure} />
              ))}
            </div>
            {part.points !== undefined && (
              <span className="shrink-0 text-sm leading-7 text-ink-500 dark:text-ink-400">
                ({formatPoints(part.points, lang)})
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
